import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

function chipColor(label) {
  if (label.startsWith("GR")) return "#BB79FB";
  return {
    "DEAR 1PM": "#FDC672",
    "KERALA 3PM": "#FD9F65",
    "DEAR 6PM": "#FC8BB2",
    "DEAR 8PM": "#73F5F8",
  }[label] || "#FDC672";
}

function istToday() {
  const ist = new Date(Date.now() + (330 + new Date().getTimezoneOffset()) * 60000);
  return ist.toISOString().slice(0, 10);
}

function shiftDate(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function Archive() {
  const [date, setDate] = useState(istToday());
  const [entries, setEntries] = useState(null);
  const [chartSnaps, setChartSnaps] = useState(null);
  const [archiveFrom, setArchiveFrom] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setChartSnaps(null);
    Promise.all([
      api.get(`/results/by-date?date=${date}`).then(({ data }) => data.entries || []).catch(() => []),
      api.get(`/archive/${date}`).then(({ data }) => data).catch(() => ({ charts: [], archive_from: null })),
    ])
      .then(([ents, arch]) => {
        if (cancelled) return;
        setEntries(ents);
        setChartSnaps(arch.charts || []);
        setArchiveFrom(arch.archive_from || null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date]);

  const pretty = new Date(`${date}T00:00:00Z`)
    .toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric", timeZone: "UTC" })
    .toUpperCase();

  return (
    <main data-testid="archive-page" className="min-h-screen bg-[#050505] text-[#FDFBF7]">
      <div className="noise-overlay" />
      <Header />
      <section className="mx-auto max-w-4xl px-6 pt-32 pb-24 md:px-12">
        <p className="mb-3 text-xs font-medium uppercase tracking-[0.3em] text-[#FFD700]">Rocky Agency</p>
        <h1 className="font-heading text-4xl font-black uppercase tracking-tight sm:text-5xl">
          Results <span className="text-[#FFD700]">Archive</span>
        </h1>
        <p className="mt-4 text-sm text-[#A1A1A6]">
          ஒரு நாளைத் தேர்ந்தெடுங்கள் — அந்த நாளின் எல்லா draw முடிவுகளும் இங்கே.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button
            data-testid="archive-prev-day"
            onClick={() => setDate((d) => shiftDate(d, -1))}
            className="rounded-full border border-white/15 px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-[#A1A1A6] transition-colors duration-300 hover:border-[#FFD700]/60 hover:text-[#FFD700]"
          >
            ← Prev
          </button>
          <input
            data-testid="archive-date-input"
            type="date"
            value={date}
            max={istToday()}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="border border-white/10 bg-black px-5 py-2 font-mono text-sm text-white outline-none focus:border-[#FFD700]/60 [color-scheme:dark]"
          />
          <button
            data-testid="archive-next-day"
            onClick={() => setDate((d) => shiftDate(d, 1))}
            disabled={date >= istToday()}
            className="rounded-full border border-white/15 px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-[#A1A1A6] transition-colors duration-300 hover:border-[#FFD700]/60 hover:text-[#FFD700] disabled:opacity-30"
          >
            Next →
          </button>
        </div>

        <p data-testid="archive-date-label" className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-[#A1A1A6]">
          {pretty}
        </p>

        {loading ? (
          <p data-testid="archive-loading" className="mt-8 text-sm text-[#666666]">Loading…</p>
        ) : entries && entries.some((e) => e.result) ? (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {entries.map((e) => (
              <div
                key={e.label}
                data-testid={`archive-card-${e.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center justify-between border border-white/10 bg-white/[0.03] px-6 py-5"
              >
                <span
                  className="rounded-full px-4 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-black"
                  style={{ backgroundColor: chipColor(e.label) }}
                >
                  {e.label}
                </span>
                <span
                  data-testid={`archive-result-${e.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`font-mono text-3xl font-black tracking-wider ${e.result ? "text-white" : "text-[#444444]"}`}
                >
                  {e.result || "—"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div data-testid="archive-empty-state" className="mt-8 border border-dashed border-white/15 p-10 text-center">
            <p className="text-sm text-[#A1A1A6]">இந்த நாளுக்கு results எதுவும் இல்லை.</p>
            <p className="mt-2 text-xs text-[#666666]">Results are recorded from 11 August 2026 onwards.</p>
          </div>
        )}

        <Link
          to="/"
          data-testid="archive-back-home"
          className="mt-12 inline-block rounded-full border border-white/15 px-6 py-2 text-xs font-medium uppercase tracking-[0.15em] text-[#A1A1A6] transition-colors duration-300 hover:border-[#FFD700]/60 hover:text-[#FFD700]"
        >
          ← Back to charts
        </Link>

        {/* full chart images as of the chosen day */}
        <div className="mt-16 border-t border-white/10 pt-10">
          <h2 className="font-heading text-2xl font-black uppercase tracking-tight sm:text-3xl">
            Charts <span className="text-[#FFD700]">as of this day</span>
          </h2>
          <p className="mt-3 text-sm text-[#A1A1A6]">
            அந்த நாள் முடிவில் charts எப்படி இருந்தனவோ அப்படியே — தினசரி result entries-உடன் தானாக சேமிக்கப்படுகின்றன.
            {archiveFrom && date < archiveFrom ? (
              <span className="mt-1 block text-xs text-[#666666]">
                Chart images archive {archiveFrom} முதல் கிடைக்கும்.
              </span>
            ) : null}
          </p>
          {loading ? null : chartSnaps && chartSnaps.length > 0 ? (
            <div data-testid="archive-charts-grid" className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {chartSnaps.map((c) => (
                <a
                  key={c.id}
                  data-testid={`archive-chart-${c.id}`}
                  href={`${process.env.REACT_APP_BACKEND_URL}${c.url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="group border border-white/10 bg-white/[0.03] transition-colors duration-300 hover:border-[#FFD700]/50"
                >
                  <div className="aspect-[3/4] overflow-hidden bg-black">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${c.url}`}
                      alt={c.title}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="px-3 py-3">
                    <p className="truncate text-[11px] font-bold uppercase tracking-wide text-white">{c.title}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-[#666666]">
                      {c.category_name}
                      {c.as_of < date ? ` • ${c.as_of} பிறகு மாற்றமில்லை` : ""}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div data-testid="archive-charts-empty" className="mt-8 border border-dashed border-white/15 p-10 text-center">
              <p className="text-sm text-[#A1A1A6]">இந்த நாளுக்கான chart images இன்னும் இல்லை.</p>
              <p className="mt-2 text-xs text-[#666666]">
                Chart snapshots இன்று முதல் தானாக சேமிக்கப்படும் — நாளை முதல் இங்கே காணலாம்.
              </p>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
